import React, { FC, PropsWithChildren } from "react";

import { ErrorBoundary } from "react-error-boundary";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { QueryClient, QueryClientProvider } from "react-query";
import { useLocation } from "react-router-dom";
import * as locales from "@mui/material/locale";
import { createTheme, StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import * as dateLocales from "@mui/x-date-pickers/locales";

import ErrorPage from "@/components/pages/ErrorPage/ErrorPage";
import queryClientDefault from "@/queryClient";
import {
    ApplicationInsightsContextProvider,
    AuthProvider,
    AuthValues,
    CookieConsentProvider,
    DialogProvider,
    GlobalFilterProvider,
    LocalFilterProvider,
    MediaDisplayerProvider,
    RouteCollectionProvider,
    ToastProvider,
    ViewModeProvider,
} from "@/shared/contexts";

import { GlobalErrorHandler } from "./globalErrorHandler";
import defaultTheme from "./styles/theme";

export type ProvidersProps = {
    defaultAuthValues?: AuthValues;
    insightsDisabled?: boolean;
    queryClient?: QueryClient;
};
export const i18nToMuiLocale = {
    en: [dateLocales.enUS, locales.enUS],
    fr: [dateLocales.frFR, locales.frFR],
    nl: [dateLocales.nlNL, locales.nlNL],
    de: [dateLocales.deDE, locales.deDE],
};

const helmetContext = {};

export const Providers: FC<PropsWithChildren<ProvidersProps>> = ({
    children,
    defaultAuthValues,
    insightsDisabled = false,
    queryClient = queryClientDefault,
}) => {
    const { i18n } = useTranslation();
    const location = useLocation();

    const themeWithLocale = React.useMemo(
        () => createTheme(defaultTheme, ...i18nToMuiLocale[i18n.language]),
        [i18n.language],
    );

    return (
        <>
            <GlobalErrorHandler>
                <HelmetProvider context={helmetContext}>
                    <QueryClientProvider client={queryClient}>
                        <StyledEngineProvider injectFirst>
                            <ThemeProvider theme={themeWithLocale}>
                                <ViewModeProvider>
                                    <ApplicationInsightsContextProvider disabled={insightsDisabled}>
                                        <ToastProvider>
                                            <DialogProvider>
                                                <ErrorBoundary resetKeys={[location.key]} FallbackComponent={ErrorPage}>
                                                    <AuthProvider defaultAuthValues={defaultAuthValues}>
                                                        <RouteCollectionProvider>
                                                            <CookieConsentProvider>
                                                                <GlobalFilterProvider>
                                                                    <LocalFilterProvider>
                                                                        <MediaDisplayerProvider>
                                                                            {children}
                                                                        </MediaDisplayerProvider>
                                                                    </LocalFilterProvider>
                                                                </GlobalFilterProvider>
                                                            </CookieConsentProvider>
                                                        </RouteCollectionProvider>
                                                    </AuthProvider>
                                                </ErrorBoundary>
                                            </DialogProvider>
                                        </ToastProvider>
                                    </ApplicationInsightsContextProvider>
                                </ViewModeProvider>
                            </ThemeProvider>
                        </StyledEngineProvider>
                    </QueryClientProvider>
                </HelmetProvider>
            </GlobalErrorHandler>
        </>
    );
};
